/**
 * Theme Utility
 *
 * Simple theme management without React Context.
 * Uses CSS variables and localStorage.
 *
 * Modes: "light" | "dark" | "system". "system" is stored explicitly and the
 * resolved theme follows prefers-color-scheme (legacy absent key = system).
 *
 * Scope:
 * - Public pages always render the OS (system) theme and do not apply the
 *   stored preference to the document.
 * - Protected routes apply the user's stored theme preference.
 */

export type Theme = "light" | "dark";
export type ThemeMode = Theme | "system";
export type ThemeScope = "public" | "protected";

const STORAGE_KEY = "theme";

/** When true, document theme follows OS only (public pages). */
let publicScope = false;

function systemTheme(): Theme {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

/**
 * Lightweight public-path check for boot-time init (avoids importing the
 * full routes config and its page modules).
 */
function isBootPublicPath(pathname: string): boolean {
  return (
    pathname === "/login" ||
    pathname === "/signup" ||
    pathname === "/unauthorized" ||
    pathname.startsWith("/tools/")
  );
}

/** Get the persisted mode. Absent/unknown values mean "system". */
export function getThemeMode(): ThemeMode {
  if (typeof window === "undefined") return "system";
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === "light" || stored === "dark" || stored === "system") {
    return stored;
  }
  return "system";
}

/** Resolve a mode to the concrete theme to render. */
export function resolveTheme(mode: ThemeMode): Theme {
  return mode === "system" ? systemTheme() : mode;
}

/** Get the resolved (rendered) theme for the active scope. */
export function getTheme(): Theme {
  if (publicScope) return systemTheme();
  return resolveTheme(getThemeMode());
}

function applyTheme(theme: Theme): void {
  document.documentElement.setAttribute("data-theme", theme);
}

/**
 * Apply theme for the current route scope.
 * Public → always system. Protected → stored user preference.
 */
export function applyThemeForScope(scope: ThemeScope): void {
  if (typeof window === "undefined") return;
  publicScope = scope === "public";
  applyTheme(getTheme());
}

/** Persist a mode; apply to the DOM only when on protected routes. */
export function setThemeMode(mode: ThemeMode): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(STORAGE_KEY, mode);
  if (!publicScope) {
    applyTheme(resolveTheme(mode));
  }
}

/**
 * Set an explicit light/dark theme.
 * @deprecated Prefer setThemeMode, which also supports "system".
 */
export function setTheme(theme: Theme): void {
  setThemeMode(theme);
}

/** Cycle light → dark → system → light. Returns the new mode. */
export function toggleTheme(): ThemeMode {
  const current = getThemeMode();
  const next: ThemeMode =
    current === "light" ? "dark" : current === "dark" ? "system" : "light";
  setThemeMode(next);
  return next;
}

/**
 * Initialize theme on app start.
 * Public paths use system; otherwise apply stored preference.
 * Keeps following OS changes while in public scope or system mode.
 */
export function initializeTheme(): void {
  if (typeof window === "undefined") return;

  applyThemeForScope(
    isBootPublicPath(window.location.pathname) ? "public" : "protected",
  );

  const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
  const handleChange = (e: MediaQueryListEvent) => {
    if (publicScope || getThemeMode() === "system") {
      applyTheme(e.matches ? "dark" : "light");
    }
  };

  mediaQuery.addEventListener("change", handleChange);
}
